import { useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FilterIcon } from '../../assets';
import { ACTION_STATUS } from '../../config/constants';
import type { ControlPiecesFilters as ControlPiecesFiltersType } from '../../types';
import { Button } from '../common/Button';
import { GenericDropdown } from '../inputs/GenericDropdown';
import { UserDropdown } from '../inputs/UserDropdown';
import { FiltersPopover } from './FiltersPopover';

type ControlPiecesFiltersFiltersProps = {
  setFilters: (val: ControlPiecesFiltersType) => void;
  filters: ControlPiecesFiltersType;
};

export const ControlPiecesFilters = ({
  setFilters,
  filters,
}: ControlPiecesFiltersFiltersProps) => {
  const { t } = useTranslation('common');
  const [open, setOpen] = useState(false);
  const options = useMemo(
    () =>
      ACTION_STATUS.map((s) => ({
        value: s,
        label: t(`actionStatus.${s}`),
      })),
    [],
  );

  // Form Methods
  const { ...methods } = useForm<ControlPiecesFiltersType>({
    defaultValues: { ...filters },
  });

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = methods;

  // Publication date
  const { has_publication_date: hasPublicationDate } = watch();

  // Reset filters for RHF values
  useEffect(() => {
    reset(filters);
  }, [filters]);

  const handleSearch = (data: ControlPiecesFiltersType) => {
    setFilters({
      ...data,
    });
    setOpen(false);
  };

  const onReset = () => {
    setFilters({});
    reset({});
  };
  return (
    <>
      <Button
        onClick={() => setOpen(true)}
        style="light-blue"
        text=""
        iconLeft={<FilterIcon />}
      />
      {open && (
        <FiltersPopover
          title={t('campaigns.filtersTitle')}
          closeModal={() => setOpen(false)}
          resetFilter={onReset}
          onSubmit={handleSubmit(handleSearch)}
        >
          <FormProvider {...methods}>
            <form
              className="flex flex-col gap-y-5"
              onSubmit={handleSubmit(handleSearch)}
            >
              {hasPublicationDate && (
                <GenericDropdown
                  isMulti
                  control={control}
                  name="status"
                  label={t('campaigns.statusFilter')}
                  placeholder={t('campaigns.statusFilterPlaceholder')}
                  options={options}
                />
              )}
              <>
                <UserDropdown
                  type="influencer"
                  label={t('campaigns.influencerLabelFilter')}
                  name="influencers"
                  multi={true}
                  control={control}
                  searchable
                  storeFullOption={true}
                />
                <UserDropdown
                  multi={true}
                  control={control}
                  storeFullOption={true}
                  searchable
                  name="client"
                  label={t('campaigns.createClient')}
                  placeholder={t('campaigns.createClientPlaceholder')}
                  type="brand"
                />
              </>
            </form>
          </FormProvider>
        </FiltersPopover>
      )}
    </>
  );
};
